import React from 'react';
import { Trash2, RefreshCw } from 'lucide-react';

/**
 * SenritsuX In-Site Branded Draft Delete Confirmation Modal
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {string} [props.draftTitle]
 * @param {boolean} props.isDeleting
 * @param {() => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
export default function DraftDeleteConfirmModal({
  isOpen,
  draftTitle = '',
  isDeleting = false,
  onConfirm,
  onCancel,
}) {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 8, 30, 0.78)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
        padding: '1rem',
        animation: 'distModalFadeIn 0.22s ease-out',
      }}
      onClick={() => {
        if (!isDeleting) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
    >
      <div
        style={{
          background: 'linear-gradient(145deg, #383494, #242168)',
          border: '1px solid rgba(255, 80, 80, 0.35)',
          borderRadius: '16px',
          padding: '2.2rem',
          maxWidth: '460px',
          width: '100%',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.65), 0 0 30px rgba(255, 75, 75, 0.1)',
          textAlign: 'center',
          position: 'relative',
          animation: 'distModalScaleIn 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Warning Icon Badge */}
        <div
          style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: 'rgba(255, 75, 75, 0.15)',
            border: '2px solid rgba(255, 75, 75, 0.35)',
            color: '#ff6b6b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.2rem',
          }}
        >
          <Trash2 size={28} />
        </div>

        {/* Heading */}
        <h3
          id="delete-modal-title"
          style={{
            fontSize: '1.35rem',
            fontWeight: 700,
            color: '#ffffff',
            margin: '0 0 0.6rem',
            letterSpacing: '0.01em',
          }}
        >
          Delete Release Draft?
        </h3>

        {/* Optional Draft Title Pill */}
        {draftTitle && (
          <div
            style={{
              display: 'inline-block',
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: '8px',
              padding: '0.35rem 0.85rem',
              fontSize: '0.85rem',
              fontWeight: 600,
              color: '#d9fb06',
              marginBottom: '1rem',
              maxWidth: '90%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {draftTitle}
          </div>
        )}

        {/* Core Confirmation Message */}
        <p
          style={{
            fontSize: '0.92rem',
            color: 'rgba(255, 255, 255, 0.82)',
            lineHeight: 1.55,
            margin: '0 0 1.8rem',
          }}
        >
          Are you sure you want to delete this draft? This cannot be undone.
        </p>

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            gap: '0.9rem',
            justifyContent: 'center',
          }}
        >
          {/* Cancel Button */}
          <button
            type="button"
            className="dist-btn-secondary"
            onClick={onCancel}
            disabled={isDeleting}
            style={{
              flex: 1,
              padding: '0.75rem 1.2rem',
              fontSize: '0.9rem',
              justifyContent: 'center',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              opacity: isDeleting ? 0.6 : 1,
            }}
          >
            Cancel
          </button>

          {/* Delete Draft Button (Visually Distinct Destructive Style) */}
          <button
            type="button"
            onClick={onConfirm}
            disabled={isDeleting}
            style={{
              flex: 1,
              padding: '0.75rem 1.2rem',
              fontSize: '0.9rem',
              fontWeight: 700,
              color: '#ffffff',
              background: isDeleting
                ? 'rgba(255, 75, 75, 0.4)'
                : 'linear-gradient(135deg, #ff4b4b 0%, #d32f2f 100%)',
              border: '1px solid rgba(255, 75, 75, 0.7)',
              borderRadius: '10px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              boxShadow: isDeleting ? 'none' : '0 4px 15px rgba(255, 75, 75, 0.35)',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              if (!isDeleting) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #ff3333 0%, #b71c1c 100%)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isDeleting) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #ff4b4b 0%, #d32f2f 100%)';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
          >
            {isDeleting ? (
              <>
                <RefreshCw size={16} className="animate-spin" /> Deleting...
              </>
            ) : (
              <>
                <Trash2 size={16} /> Delete Draft
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}